/**
 * Access Management — who holds which system role.
 *
 * Two rules shape this whole screen, and both exist to protect live work:
 * there may never be more than three active Finance Approvers, and Main Access
 * is never assigned directly — it moves only through a transfer confirmed by a
 * one-time code emailed to the current holder.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ACCESS,
  ACCESS_LABEL,
  MAX_ACTIVE_FINANCE_HOLDERS,
  type AccessLevel,
  type User,
} from '@gdec/shared';
import { api } from '@/api/client';
import { useAuth, useCurrentUser } from '@/auth/AuthContext';
import { EmptyState } from '@/components/EmptyState';
import { ErrorNote, useAction } from '@/components/QueueKit';
import { LoadingBlock } from '@/components/Spinner';
import { useToast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { financeCapReached, usersWithAccess } from '@/components/admin/helpers';
import { useDirectory } from '@/components/admin/useDirectory';
import { Modal } from '@/components/Modal';
import '@/styles/pages.css';

interface RoleSection {
  access: AccessLevel;
  blurb: string;
  cap?: number;
}

const SECTIONS: RoleSection[] = [
  {
    access: ACCESS.MAIN_ACCESS,
    blurb:
      'Full administrative control. Any number of people may hold it, but at least one always must.',
  },
  {
    access: ACCESS.FINANCE_REVIEW,
    blurb:
      'Reviews, approves and releases. Sets the amount, records the payment reference, and completes the claim — the Finance Approver and Treasury roles were merged into this one.',
    cap: MAX_ACTIVE_FINANCE_HOLDERS,
  },
  {
    access: ACCESS.EXECUTIVE_APPROVER,
    blurb: 'Approves claims from anyone without a manager above them.',
  },
  {
    access: ACCESS.LEAD_APPROVER,
    blurb: 'The "Approved By" step for claims already checked by a manager.',
  },
  {
    access: ACCESS.MANAGER_APPROVER,
    blurb: 'The "Checked By" step for their own direct reports.',
  },
];

export function AccessManagementPage(): ReactNode {
  const me = useCurrentUser();
  const { toast } = useToast();
  const { refreshIfSelf } = useAuth();
  const { users, loading, error, reload } = useDirectory();

  const [assigning, setAssigning] = useState<{ user: User; access: AccessLevel } | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const active = useMemo(() => users.filter((u) => u.active), [users]);

  const applyAccess = useCallback(async (): Promise<void> => {
    if (!assigning) return;
    await api.admin.setSystemAccess<unknown>({
      userId: assigning.user.id,
      systemAccess: assigning.access,
    });
    toast(`${assigning.user.name} is now ${ACCESS_LABEL[assigning.access]}.`, 'ok');
    setAssigning(null);
    await reload();
    // Your own role decides which queues the sidebar offers, so the session has
    // to catch up or the app keeps showing the access you no longer hold.
    await refreshIfSelf(assigning.user.id);
  }, [assigning, toast, reload, refreshIfSelf]);

  if (loading) return <LoadingBlock label="Loading the directory…" />;

  const capReached = financeCapReached(active);

  return (
    <>
      <ErrorNote message={error} />

      <div className="panel">
        <h3>Access Management</h3>
        <p className="hint">
          A person holds exactly one system role. Changing it takes effect on their next request —
          and is refused while they still own claims in progress at their current stage.
        </p>
        {/*
          This screen is organised by role, so it shows only the people who hold
          one — every ordinary Requestor is absent, which is most of the company.
          Saying where the rest of employee administration lives is cheaper than
          letting someone conclude an employee cannot be found or reset at all.
        */}
        <p className="hint">
          Only role holders are listed here. To reset a password, or to reach any other
          employee record, use the <Link to="/org-chart">Organization Chart</Link> — it lists
          everyone and is searchable by name.
        </p>
      </div>

      {SECTIONS.map((section) => {
        const holders = usersWithAccess(active, section.access);
        const atCap = section.cap !== undefined && holders.length >= section.cap;

        return (
          <div className="panel" key={section.access}>
            <h3>
              <span className="title">
                {ACCESS_LABEL[section.access]}
                <span className="tag-chip">
                  {section.cap === undefined
                    ? holders.length
                    : `${holders.length} / ${section.cap}`}
                </span>
              </span>
            </h3>
            <p className="hint">{section.blurb}</p>

            {holders.length === 0 ? (
              <EmptyState icon="🔑" title={`Nobody holds ${ACCESS_LABEL[section.access]}.`} />
            ) : (
              <ul className="role-list">
                {holders.map((u) => (
                  <li key={u.id}>
                    <div>
                      <b>{u.name}</b>
                      <span className="role-sub">
                        {u.position} · {u.department} · {u.empId}
                      </span>
                    </div>
                    {section.access === ACCESS.MAIN_ACCESS && u.id === me.id ? (
                      <span className="muted">You</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setAssigning({ user: u, access: ACCESS.REQUESTOR })}
                      >
                        Remove role
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {section.access === ACCESS.MAIN_ACCESS ? (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => setTransferOpen(true)}
              >
                Transfer Main Access…
              </button>
            ) : (
              <AssignControl
                candidates={active.filter((u) => u.systemAccess !== section.access)}
                disabled={atCap && section.access === ACCESS.FINANCE_REVIEW && capReached}
                disabledReason={
                  section.access === ACCESS.FINANCE_REVIEW && capReached
                    ? `All ${MAX_ACTIVE_FINANCE_HOLDERS} Finance seats are taken. Remove one first.`
                    : undefined
                }
                onAssign={(user) => setAssigning({ user, access: section.access })}
              />
            )}
          </div>
        );
      })}

      {assigning ? (
        <ConfirmDialog
          title={`Change ${assigning.user.name}'s access`}
          confirmLabel="Change access"
          tone={assigning.access === ACCESS.REQUESTOR ? 'danger' : 'primary'}
          onCancel={() => setAssigning(null)}
          onConfirm={applyAccess}
        >
          <p>
            {assigning.user.name} becomes <b>{ACCESS_LABEL[assigning.access]}</b>, replacing{' '}
            {ACCESS_LABEL[assigning.user.systemAccess]}.
          </p>
          <p className="hint">
            If they still own claims in progress at their current stage, this will be refused —
            reassign or complete those first.
          </p>
        </ConfirmDialog>
      ) : null}

      {transferOpen ? (
        <TransferMainAccess
          candidates={active.filter((u) => u.id !== me.id)}
          currentHolder={me}
          onDone={async () => {
            setTransferOpen(false);
            await reload();
          }}
          onCancel={() => setTransferOpen(false)}
        />
      ) : null}
    </>
  );
}

function AssignControl({
  candidates,
  disabled,
  disabledReason,
  onAssign,
}: {
  candidates: readonly User[];
  disabled: boolean;
  disabledReason?: string;
  onAssign: (user: User) => void;
}): ReactNode {
  const [selected, setSelected] = useState('');

  return (
    <div className="assign-row">
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={disabled}
        aria-label="Employee to assign"
      >
        <option value="">Add someone…</option>
        {candidates.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name} — {u.department}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        disabled={disabled || selected === ''}
        onClick={() => {
          const user = candidates.find((u) => u.id === selected);
          if (user) {
            onAssign(user);
            setSelected('');
          }
        }}
      >
        Assign
      </button>
      {disabledReason ? <span className="field-error">{disabledReason}</span> : null}
    </div>
  );
}

/**
 * The two-step Main Access transfer.
 *
 * The code is emailed to the *current* holder and never returned by the API —
 * the response says only which address it went to. There is deliberately
 * nothing here that could display it.
 */
function TransferMainAccess({
  candidates,
  currentHolder,
  onDone,
  onCancel,
}: {
  candidates: readonly User[];
  currentHolder: User;
  onDone: () => Promise<void>;
  onCancel: () => void;
}): ReactNode {
  const { toast } = useToast();
  const action = useAction();
  const [targetId, setTargetId] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const target = candidates.find((u) => u.id === targetId) ?? null;

  const requestCode = useCallback(async (): Promise<void> => {
    if (!target) return;
    const ok = await action.run(async () => {
      const res = await api.admin.requestMainAccessTransfer<{ sentTo: string }>({
        targetUserId: target.id,
      });
      setSentTo(res.sentTo);
    });
    if (ok) toast('A confirmation code has been emailed to you.', 'info');
  }, [target, action, toast]);

  const confirm = useCallback(async (): Promise<void> => {
    if (!target) return;
    const ok = await action.run(async () => {
      await api.admin.confirmMainAccessTransfer<unknown>({
        targetUserId: target.id,
        code: code.trim(),
      });
    });
    if (!ok) return;
    toast(`Main Access transferred to ${target.name}.`, 'ok');
    await onDone();
  }, [target, code, action, toast, onDone]);

  return (
    <Modal
      title="Transfer Main Access"
      onClose={action.busy ? () => undefined : onCancel}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={action.busy}>
            Cancel
          </button>
          {sentTo === null ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void requestCode()}
              disabled={action.busy || target === null}
            >
              Email me a code
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => void confirm()}
              disabled={action.busy || code.trim().length < 6}
            >
              Transfer Main Access
            </button>
          )}
        </>
      }
    >
      <ErrorNote message={action.error} />

      <p className="dialog-lead">
        There is only ever one Main Access holder. Completing this transfer moves it away from you —
        your own access becomes <b>Executive Approver</b> immediately, and you will no longer be
        able to reach the administration screens.
      </p>

      <div className="field">
        <label htmlFor="ma-target">Transfer to</label>
        <select
          id="ma-target"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          disabled={sentTo !== null || action.busy}
        >
          <option value="">Choose an employee…</option>
          {candidates.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} — {u.position}, {u.department}
            </option>
          ))}
        </select>
      </div>

      {sentTo === null ? (
        <p className="hint">
          A six-digit code will be emailed to {currentHolder.email}. You will need it to complete
          the transfer.
        </p>
      ) : (
        <div className="field">
          <label htmlFor="ma-code">Confirmation code</label>
          <input
            id="ma-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="6-digit code"
          />
          <p className="hint">
            Sent to {sentTo}. It expires in ten minutes and can be used once. If it has not arrived,
            cancel and start again.
          </p>
        </div>
      )}
    </Modal>
  );
}
